// Simulation Template Structure:
/*
Template Components:

1. SimulationTemplate
   Props:
   - title: string
   - description: string
   - children: React.ReactNode
   Structure:
   - Header with title and description
   - Main content area for simulation stages

2. SimulationStep
   Props:
   - title: string
   - isActive?: boolean (default: true)
   - children: React.ReactNode
   Styling:
   - Backdrop blur
   - Rounded corners
   - Border
   - Opacity change when inactive

3. ResultBlock
   Props:
   - title: string
   - icon: LucideIcon
   - variant: 'default' | 'highlight' | 'muted' | 'info'
   - children: React.ReactNode
   Variants:
   - default: white background, gray border
   - highlight: orange background, orange border
   - muted: gray background, gray border
   - info: blue background, blue border

   Required Result Section Titles:
   1. "Understanding Your Results" (variant: highlight)
   2. "In the Original Study" (variant: info)
   3. "About the Study" (variant: muted)

4. ResultMessage
   Props:
   - isCorrect: boolean
   - message: React.ReactNode | string
   Styling:
   - Green background with check icon for correct
   - Amber background with cross icon for incorrect

5. GraphContainer
   Props:
   - children: React.ReactNode
   Styling:
   - White background
   - Horizontally scrollable on mobile
   - Full width on desktop

Common State Structure:
{
  stage: string;          // Current stage of simulation
  started: boolean;       // Whether simulation has started
  showResults: boolean;   // Whether to show results
  userChoices: any;      // User's selections/inputs
  isSimulating: boolean; // Whether simulation is running
}

Typical Flow:
1. Start button
2. Simulation steps (using SimulationStep)
3. Results section (using ResultBlock)
4. Reset/Try Again option
*/

import React from 'react';
import { Text } from '../ui/typography/Text';
import { LucideIcon, Check, X } from 'lucide-react';

interface SimulationTemplateProps {
  // Introduction section
  title: string;
  description: string;

  // Main content
  children: React.ReactNode;
}

// Wrapper components for consistent styling
export const SimulationStep: React.FC<{
  title: string;
  isActive?: boolean;
  children: React.ReactNode;
}> = ({ title, isActive = true, children }) => (
  <div className={`bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-100 transition-all duration-500 ${
    !isActive ? 'opacity-70 pointer-events-none' : ''
  }`}>
    <Text size="xl" className="font-medium text-gray-800 mb-4">
      {title}
    </Text>
    {children}
  </div>
);

// Component for displaying feedback messages with check/cross icons
export const ResultMessage: React.FC<{
  isCorrect: boolean;
  message: React.ReactNode | string;
}> = ({ isCorrect, message }) => {
  const bgColor = isCorrect ? 'bg-green-50' : 'bg-red-50';
  const textColor = isCorrect ? 'text-green-700' : 'text-red-700';
  const borderColor = isCorrect ? 'border-green-100' : 'border-red-100';
  const Icon = isCorrect ? Check : X;
  
  return (
    <div className={`p-4 rounded-lg ${bgColor} border ${borderColor} flex items-start gap-3`}>
      <div className={`flex-shrink-0 p-1 rounded-full ${isCorrect ? 'bg-green-100' : 'bg-red-100'} mt-0.5`}>
        <Icon size={16} className={textColor} />
      </div>
      <div className={textColor}>
        {message}
      </div>
    </div>
  );
};

// Container for graphs with proper mobile responsiveness
export const GraphContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
      <div className="w-full">
        {children}
      </div>
    </div>
  );
};

export const ResultBlock: React.FC<{
  title: string;
  icon: LucideIcon;
  variant?: 'default' | 'highlight' | 'muted' | 'info';
  children: React.ReactNode;
}> = ({ title, icon: Icon, variant = 'default', children }) => {
  const variantStyles = {
    default: 'bg-white/80 border-gray-100',
    highlight: 'bg-orange-50/80 border-orange-100',
    muted: 'bg-gray-50/80 border-gray-100',
    info: 'bg-blue-50/80 border-blue-100'
  };

  const iconStyles = {
    default: 'bg-gray-100 text-gray-600',
    highlight: 'bg-orange-100 text-orange-600',
    muted: 'bg-gray-100 text-gray-600',
    info: 'bg-blue-100 text-blue-600'
  };

  return (
    <div className={`backdrop-blur-sm rounded-2xl p-4 sm:p-6 border ${variantStyles[variant]}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${iconStyles[variant]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <Text size="xl" className="font-medium text-gray-800">
          {title}
        </Text>
      </div>
      {children}
    </div>
  );
};

export const SimulationTemplate: React.FC<SimulationTemplateProps> = ({
  title,
  description,
  children
}) => {
  return (
    <>
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-100 mb-8">
        <Text size="xl" className="font-semibold text-gray-800 mb-4">{title}</Text>
        <Text className="text-gray-700">{description}</Text>
      </div>
      {children}
    </>
  );
}; 